import {
    CameraMode,
    DocumentUid,
    EditorModes,
    EnterpriseHandle,
    OrganizationHandle,
    OrganizationPrivateHandle,
    Slug,
    slugifyDocumentName,
    UserHandle,
} from "@buildwithflux/models";

import {ReverseUrlRoutingError, UrlParsingError} from "./error";
import {isSystemRoute} from "./handle";
import {DocumentDataForUrlRouting} from "./models";
import {toAbsoluteUrl} from "./origin";
import {joinPathParts} from "./path";
import {isLocalSlug} from "./slug";

const PROJECT_BY_UID = "/projects/:documentUid";

/**
 * Reserved sub-paths directly below a project that map to specific editor views.
 * These form a closed set — any path segment immediately after the project slug that matches
 * one of these is treated as an editor sub-path, not a local component slug.
 */
export const RESERVED_EDITOR_SUBPATHS = ["schematic", "code", "pcb", "files", "parts", "history", "nets", "components", "bom"] as const;
export type ReservedEditorSubpath = (typeof RESERVED_EDITOR_SUBPATHS)[number];

/**
 * A list of all possible document routes
 *
 * Order matters here, otherwise 'enterprise' may be interpreted as an owner handle, the enterprise handle interpreted
 * as a document slug, and the project slug interpreted as a local component slug etc.
 *
 * History routes are placed before their base routes to ensure they match first when the /history suffix is present.
 * Editor sub-path routes (e.g. /schematic, /pcb/2d) are matched via catch-all segments rather than explicit route
 * entries — parseDocumentPathname() handles the sub-path extraction.
 */
export const DocumentRoutes: string[] = [
    // Enterprise with non-default org: history sub-path routes (including two-part editor like pcb/2d)
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug/history/:historyId/:editorSubPath/:editorDetail",
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug/history/:historyId/:editorSubPath",
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug/history/:historyId",
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug/history",
    // Enterprise with non-default org: editor sub-path routes
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug/:editorSubPath/:editorDetail",
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug/:editorSubPath",
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:documentSlug",
    // Enterprise with non-default org: legacy local component route (structurally shadowed by
    // :editorSubPath catch-all above, but kept for documentation and React Router devtools clarity)
    "/enterprise/:enterpriseHandle/-/:organizationHandle/:baseSlug/:componentSlug",
    // Enterprise with default org: history sub-path routes (including two-part editor like pcb/2d)
    "/enterprise/:enterpriseHandle/:documentSlug/history/:historyId/:editorSubPath/:editorDetail",
    "/enterprise/:enterpriseHandle/:documentSlug/history/:historyId/:editorSubPath",
    "/enterprise/:enterpriseHandle/:documentSlug/history/:historyId",
    "/enterprise/:enterpriseHandle/:documentSlug/history",
    // Enterprise with default org: editor sub-path routes
    "/enterprise/:enterpriseHandle/:documentSlug/:editorSubPath/:editorDetail",
    "/enterprise/:enterpriseHandle/:documentSlug/:editorSubPath",
    "/enterprise/:enterpriseHandle/:documentSlug",
    // Enterprise with default org: legacy local component route (structurally shadowed by
    // :editorSubPath catch-all above, but kept for documentation and React Router devtools clarity)
    "/enterprise/:enterpriseHandle/:baseSlug/:componentSlug",
    // UID-based route
    PROJECT_BY_UID,
    // User/org: history sub-path routes (including two-part editor like pcb/2d)
    "/:handle/:documentSlug/history/:historyId/:editorSubPath/:editorDetail",
    "/:handle/:documentSlug/history/:historyId/:editorSubPath",
    "/:handle/:documentSlug/history/:historyId",
    "/:handle/:documentSlug/history",
    // User/org: editor sub-path routes
    "/:handle/:documentSlug/:editorSubPath/:editorDetail",
    "/:handle/:documentSlug/:editorSubPath",
    "/:handle/:documentSlug",
    // User/org: legacy local component route (structurally shadowed by
    // :editorSubPath catch-all above, but kept for documentation and React Router devtools clarity)
    "/:handle/:baseSlug/:componentSlug",
];

type DocumentUrlParams = {
    editorMode?: EditorModes;
    cameraMode?: CameraMode;
    embed?: boolean | undefined;

    /**
     * Used to link directly to a designator in a project
     */
    fit?: string;

    /**
     * Used to link directly to a specific project doc title slug.
     * @deprecated Use editorSubPath with "files/{slug}" instead
     */
    doc?: string;

    /**
     * Path-based editor sub-path to append to the project URL.
     * E.g. "schematic", "pcb/2d", "code", "files", "files/my-doc", "parts/resistor"
     */
    editorSubPath?: string;
};

interface GetDocumentUrlOptions {
    baseSlugForLocalComponent?: string | undefined;
    urlParams?: DocumentUrlParams;
    /**
     * When true, appends the `~shortId` suffix to generated document URLs.
     * Defaults to true — short IDs are included whenever the document has one.
     * Pass `false` to explicitly suppress the suffix (e.g. in tests or migrations).
     */
    enableShortIdUrls?: boolean;
}

/**
 * Type representing every kind of document URL that can be parsed (e.g. will always either have a UID or a slug when
 *  parsing is successful, but the owner details may vary)
 */
export type ParsedDocumentPath =
    | {
          uid: DocumentUid;
          /** Editor sub-path from segments after the UID, e.g. "schematic", "pcb/2d", "files/my-doc" */
          editorSubPath: string | undefined;
          /** History ID from /projects/{uid}/history/{id}/... */
          historyId: string | undefined;
          /** Short UID prefix from a file URL like /files/doc-slug~{uidPart} */
          docUidPart: string | undefined;
      }
    | ({
          slug: Slug;
          localComponentSlug: Slug | undefined;
          /**
           * The editor sub-path extracted from the URL, if a reserved sub-path was found.
           * E.g. "schematic", "pcb", "pcb/2d", "pcb/3d", "code", "files", "files/my-doc",
           * "parts", "parts/resistor"
           */
          editorSubPath: string | undefined;
          /**
           * History ID extracted from /history/{historyId} in the URL, if present.
           * Undefined when not viewing history or when history mode shows "all"/"latest".
           */
          historyId: string | undefined;
          /**
           * Short UID prefix extracted from a file URL like /files/doc-slug~{uidPart}.
           * Used to disambiguate documents with identical slugified names.
           * Undefined when the URL has no UID suffix (legacy format).
           */
          docUidPart: string | undefined;
          /**
           * Short ID extracted from the project slug suffix: `/{handle}/{slug}~{shortId}`.
           * This is the Feistel-based short ID assigned at document creation time.
           * Undefined for legacy URLs without a short ID suffix.
           */
          shortId: string | undefined;
      } & (
          | Record<string, never>
          | {userOrOrganizationHandle: UserHandle | OrganizationHandle}
          | {enterpriseHandle: EnterpriseHandle; enterpriseOrganizationHandle: OrganizationHandle | undefined}
      ));

/**
 * Length of the short UID prefix appended to file URLs for disambiguation.
 * 8 hex characters gives ~4 billion unique values, sufficient for doc collections.
 */
export const DOC_UID_SHORT_LENGTH = 8;

/**
 * The separator between a document slug and its short ID in URLs.
 * E.g. `/{handle}/{slug}~{shortId}`
 *
 * This character is URL-safe, not in the slug alphabet `[a-z0-9-]`,
 * and enables unambiguous parsing.
 */
export const SHORT_ID_SEPARATOR = "~";

/**
 * Extracts the short ID suffix from a document slug.
 * Given "audio-amplifier~x9", returns { slug: "audio-amplifier", shortId: "x9" }.
 * Given "audio-amplifier" (no tilde), returns { slug: "audio-amplifier", shortId: undefined }.
 *
 * The short ID is the portion after the last `~` in the slug segment.
 * A bare tilde at the end (e.g. "slug~") is treated as no short ID.
 */
export function extractShortIdFromSlug(rawSlug: string): { slug: string; shortId: string | undefined } {
    const tildeIndex = rawSlug.lastIndexOf(SHORT_ID_SEPARATOR);
    if (tildeIndex === -1) {
        return { slug: rawSlug, shortId: undefined };
    }
    const shortId = rawSlug.slice(tildeIndex + 1);
    if (shortId.length === 0) {
        return { slug: rawSlug.slice(0, tildeIndex), shortId: undefined };
    }
    return { slug: rawSlug.slice(0, tildeIndex), shortId };
}

/**
 * Formats a document slug with an optional short ID suffix.
 * "audio-amplifier" + "x9" → "audio-amplifier~x9"
 * "audio-amplifier" + undefined → "audio-amplifier"
 */
function formatSlugWithShortId(slug: string, shortId: string | undefined): string {
    // Falsy check intentionally treats both undefined and empty string as "no short ID"
    if (shortId) {
        return `${slug}${SHORT_ID_SEPARATOR}${shortId}`;
    }
    return slug;
}

/**
 * Main function for getting the absolute canonical URL for a document
 */
export function getAbsoluteDocumentUrl(
    document: DocumentDataForUrlRouting,
    options: GetDocumentUrlOptions = {},
): string {
    return toAbsoluteUrl(getDocumentUrl(document, options));
}

/**
 * Main function for getting the relative canonical URL for a document
 */
export function getDocumentUrl(document: DocumentDataForUrlRouting, options: GetDocumentUrlOptions = {}): string {
    /*
     * Requirements for reverse routing by slug:
     *  - For local components: need to have a baseSlugForLocalComponent passed to this function to receive routing based
     *      on their slug; we can't resolve the required base slug from the IDocumentData itself
     *  - For enterprise-owned documents: need to have the enterprise_owner_handle set on the IDocumentData
     *  - For all documents: need to have a slug and owner_handle set
     *
     * If any of these aren't the case, we can use the fallback of routing by UID (as long as the document has a UID)
     */
    if (
        (document.enterprise_owner_uid && !document.enterprise_owner_handle) ||
        ((document.localToDocumentUid || isLocalSlug(document.slug)) && !options.baseSlugForLocalComponent) ||
        !document.slug ||
        !document.owner_handle
    ) {
        if (!document.uid) {
            throw new Error("Could not generate URL for project with no UID");
        }

        return getDocumentUrlFromUid(document.uid, options);
    }

    /*
     * The normal case: build a URL using slugs of various kinds
     */
    return getDocumentUrlFromSlug(document, options);
}

/**
 * Relative canonical slug URL for storing in a search index, or undefined when
 * the document can only be routed by UID. Search records intentionally omit the
 * UID fallback (it is an SEO antipattern) rather than persist a non-canonical link.
 */
export function getCanonicalProjectUrlForSearch(
    document: DocumentDataForUrlRouting,
    options: GetDocumentUrlOptions = {},
): string | undefined {
    if (
        (document.enterprise_owner_uid && !document.enterprise_owner_handle) ||
        ((document.localToDocumentUid || isLocalSlug(document.slug)) && !options.baseSlugForLocalComponent) ||
        !document.slug ||
        !document.owner_handle
    ) {
        return undefined;
    }

    return getDocumentUrlFromSlug(document, options);
}

/**
 * Checks whether a path segment is a reserved editor sub-path.
 */
export function isReservedEditorSubpath(segment: string): segment is ReservedEditorSubpath {
    return (RESERVED_EDITOR_SUBPATHS as readonly string[]).includes(segment);
}

/**
 * Extracts the short UID part from a file editor sub-path like "files/doc-slug~abc12345".
 * Returns the UID part string if found, undefined otherwise.
 */
function extractDocUidPart(editorSubPath: string): string | undefined {
    if (!editorSubPath.startsWith("files/")) {
        return undefined;
    }
    const fileSlugPart = editorSubPath.slice("files/".length);
    const tildeIndex = fileSlugPart.lastIndexOf("~");
    if (tildeIndex === -1) {
        return undefined;
    }
    const uidPart = fileSlugPart.slice(tildeIndex + 1);
    // Sanity check: UID part should be non-empty and look like a hex prefix
    if (uidPart.length > 0) {
        return uidPart;
    }
    return undefined;
}

/**
 * Strips the UID suffix from a file editor sub-path, returning just the slug portion.
 * "files/doc-slug~abc12345" → "doc-slug"
 * "files/doc-slug" → "doc-slug"
 */
export function stripDocUidFromSubPath(editorSubPath: string): string {
    if (!editorSubPath.startsWith("files/")) {
        return editorSubPath;
    }
    const fileSlugPart = editorSubPath.slice("files/".length);
    const tildeIndex = fileSlugPart.lastIndexOf("~");
    if (tildeIndex === -1) {
        return fileSlugPart;
    }
    return fileSlugPart.slice(0, tildeIndex);
}

/**
 * Extracts the editor sub-path and optional detail from path segments that follow
 * the project slug. Returns the parsed sub-path info and the remaining segments.
 *
 * For reserved sub-paths:
 *   - "schematic" → editorSubPath: "schematic"
 *   - "pcb" → editorSubPath: "pcb"
 *   - "pcb/2d" → editorSubPath: "pcb/2d"
 *   - "pcb/3d" → editorSubPath: "pcb/3d"
 *   - "files" → editorSubPath: "files"
 *   - "files/my-doc" → editorSubPath: "files/my-doc"
 *   - "parts" → editorSubPath: "parts"
 *   - "parts/resistor" → editorSubPath: "parts/resistor", localComponentSlug: "resistor"
 *   - "code" → editorSubPath: "code"
 *   - "history" → historyId: undefined (bare history mode)
 *   - "history/abc123" → historyId: "abc123"
 *   - "history/abc123/pcb" → historyId: "abc123", editorSubPath: "pcb"
 *
 * For non-reserved segments (legacy local component URLs):
 *   - "resistor" → localComponentSlug: "resistor" (backward compat)
 */
function parseEditorSubPathSegments(segments: string[]): {
    editorSubPath: string | undefined;
    localComponentSlug: Slug | undefined;
    historyId: string | undefined;
    docUidPart: string | undefined;
} {
    if (segments.length === 0) {
        return {editorSubPath: undefined, localComponentSlug: undefined, historyId: undefined, docUidPart: undefined};
    }

    const firstSegment = segments[0]!;

    // Handle history sub-path: /history, /history/{id}, /history/{id}/{editor}
    if (firstSegment === "history") {
        const historyId = segments[1] || undefined;
        // If there's an editor sub-path after the history ID
        if (segments.length >= 3 && historyId) {
            const editorSegment = segments[2]!;
            const editorDetail = segments[3];
            // NOTE: The editor segment after a history ID is intentionally not validated
            // against RESERVED_EDITOR_SUBPATHS — callers should use editorStateForEditorSubPath()
            // to check whether the returned editorSubPath is meaningful.
            const editorSubPath = editorDetail ? `${editorSegment}/${editorDetail}` : editorSegment;
            const docUidPart = extractDocUidPart(editorSubPath);
            return {editorSubPath, localComponentSlug: undefined, historyId, docUidPart};
        }
        return {editorSubPath: undefined, localComponentSlug: undefined, historyId, docUidPart: undefined};
    }

    if (!isReservedEditorSubpath(firstSegment)) {
        // Legacy local component URL: /:handle/:baseSlug/:componentSlug
        return {editorSubPath: undefined, localComponentSlug: firstSegment, historyId: undefined, docUidPart: undefined};
    }

    const detail = segments[1];

    if (firstSegment === "parts" && detail) {
        // /parts/{subpart} → the subpart is also the local component slug
        return {
            editorSubPath: `parts/${detail}`,
            localComponentSlug: detail,
            historyId: undefined,
            docUidPart: undefined,
        };
    }

    if (detail) {
        // e.g. pcb/2d, pcb/3d, files/my-doc, files/my-doc~abc12345
        const fullSubPath = `${firstSegment}/${detail}`;
        const docUidPart = extractDocUidPart(fullSubPath);
        return {
            editorSubPath: fullSubPath,
            localComponentSlug: undefined,
            historyId: undefined,
            docUidPart,
        };
    }

    // Single segment: schematic, code, pcb, files, parts
    return {editorSubPath: firstSegment, localComponentSlug: undefined, historyId: undefined, docUidPart: undefined};
}

/**
 * Parses a pathname (e.g. `location.pathname` or `new URL("...").pathname`) to extract the document information
 *
 * Supports all the same URLS generated by `getDocumentUrl`:
 *  - /projects/:uid
 * And for these formats, supports additional sub-paths for editor mode, history, and local components:
 *  - /:userOrOrganizationHandle/:slug[/editorSubPath]
 *  - /enterprise/:enterpriseHandle/:slug[/editorSubPath]
 *  - /enterprise/:enterpriseHandle/-/:enterpriseOrganizationHandle/:slug[/editorSubPath]
 *
 * Reserved editor sub-paths: schematic, code, pcb, files, parts, history
 * These are distinguished from legacy local component slugs (/:handle/:baseSlug/:componentSlug)
 * by checking against the RESERVED_EDITOR_SUBPATHS set.
 *
 * To load a document from the parsed information, see loadDocumentBaseDataFromParsedPath()
 */
export function parseDocumentPathname(pathname: string): ParsedDocumentPath {
    const parts = pathname.split("/").filter((part) => part.length > 0);

    if (parts?.[0] === "projects") {
        const [_projectsPrefix, documentUid, ...rest] = parts;

        const documentUidParsed = DocumentUid.safeParse(documentUid);

        if (!documentUidParsed.success) {
            throw new UrlParsingError("Invalid document UID in pathname", documentUidParsed.error);
        }

        // Parse sub-path segments after the UID (e.g. /projects/{uid}/history/{id}/schematic)
        const {editorSubPath, historyId, docUidPart} = parseEditorSubPathSegments(rest);

        return {uid: documentUidParsed.data, editorSubPath, historyId, docUidPart};
    }

    if (parts?.[0] === "enterprise" && parts?.[2] === "-") {
        // /enterprise/:enterpriseHandle/-/:organizationHandle/:slug[~shortId][/subPath...]
        const [_enterprisePrefix, enterpriseHandle, _dash, enterpriseOrganizationHandle, rawSlug, ...rest] = parts;

        if (!rawSlug) {
            throw new UrlParsingError("Missing slug in pathname, expected in fourth position");
        }

        const enterpriseHandleParsed = EnterpriseHandle.safeParse(enterpriseHandle);

        if (!enterpriseHandleParsed.success) {
            throw new UrlParsingError("Invalid enterprise handle in pathname", enterpriseHandleParsed.error);
        }

        const enterpriseOrganizationHandleParsed = OrganizationPrivateHandle.safeParse(enterpriseOrganizationHandle);

        if (!enterpriseOrganizationHandleParsed.success) {
            throw new UrlParsingError(
                "Invalid enterprise handle in pathname",
                enterpriseOrganizationHandleParsed.error,
            );
        }

        const {slug, shortId} = extractShortIdFromSlug(rawSlug);
        const {editorSubPath, localComponentSlug, historyId, docUidPart} = parseEditorSubPathSegments(rest);

        return {
            enterpriseHandle: enterpriseHandleParsed.data,
            enterpriseOrganizationHandle: enterpriseOrganizationHandleParsed.data,
            slug,
            localComponentSlug,
            editorSubPath,
            historyId,
            docUidPart,
            shortId,
        };
    }

    if (parts?.[0] === "enterprise") {
        // /enterprise/:enterpriseHandle/:slug[~shortId][/subPath...]
        const [_enterprisePrefix, enterpriseHandle, rawSlug, ...rest] = parts;

        if (!rawSlug) {
            throw new UrlParsingError("Missing slug in pathname, expected in third position");
        }

        const enterpriseHandleParsed = EnterpriseHandle.safeParse(enterpriseHandle);

        if (!enterpriseHandleParsed.success) {
            throw new UrlParsingError("Invalid enterprise handle in pathname", enterpriseHandleParsed.error);
        }

        const {slug, shortId} = extractShortIdFromSlug(rawSlug);
        const {editorSubPath, localComponentSlug, historyId, docUidPart} = parseEditorSubPathSegments(rest);

        return {
            enterpriseHandle: enterpriseHandleParsed.data,
            enterpriseOrganizationHandle: undefined,
            slug,
            localComponentSlug,
            editorSubPath,
            historyId,
            docUidPart,
            shortId,
        };
    }

    // /:handle/:slug[~shortId][/subPath...]
    const [userOrOrganizationHandle, rawSlug, ...rest] = parts;

    if (!userOrOrganizationHandle || !rawSlug) {
        throw new UrlParsingError("Pathname must have at least two parts to be a valid document pathname");
    }

    const {slug, shortId} = extractShortIdFromSlug(rawSlug);
    const {editorSubPath, localComponentSlug, historyId, docUidPart} = parseEditorSubPathSegments(rest);

    return {
        userOrOrganizationHandle,
        slug,
        localComponentSlug,
        editorSubPath,
        historyId,
        docUidPart,
        shortId,
    };
}

/**
 * Extracts a document identifier from a pathname if it matches a document URL pattern.
 *
 * Returns the cleaned pathname (without leading / and without editor sub-paths or history suffixes)
 * suitable for passing to document resolution functions. Returns undefined if the pathname doesn't
 * match any document URL pattern.
 */
export function getDocumentIdentifierFromPathname(pathname: string): string | undefined {
    try {
        const parsed = parseDocumentPathname(pathname);
        // parseDocumentPathname accepts any 2+ segment path as /:handle/:slug,
        // so for that case, additionally verify it's not a known system route
        if ("uid" in parsed) {
            return parsed.uid;
        }
        if ("userOrOrganizationHandle" in parsed && isSystemRoute(pathname)) {
            return undefined;
        }
        // Build the base path from parsed components, stripping editor sub-paths and history
        const basePath = getBasePathFromParsed(parsed);
        // Preserve local component slug so callers (e.g. RestoreProjectDialog) resolve
        // the correct document, not just the base project.
        if (parsed.localComponentSlug) {
            return `${basePath}/${parsed.localComponentSlug}`;
        }
        return basePath;
    } catch {
        return undefined;
    }
}

/**
 * Builds the base document path (handle/slug) from a parsed document path,
 * stripping any editor sub-paths and history suffixes.
 */
function getBasePathFromParsed(
    parsed: Exclude<ParsedDocumentPath, {uid: DocumentUid}>,
): string {
    const slugWithShortId = formatSlugWithShortId(parsed.slug, parsed.shortId);
    if ("enterpriseHandle" in parsed) {
        if (parsed.enterpriseOrganizationHandle) {
            return `enterprise/${parsed.enterpriseHandle}/-/${parsed.enterpriseOrganizationHandle}/${slugWithShortId}`;
        }
        return `enterprise/${parsed.enterpriseHandle}/${slugWithShortId}`;
    }
    if ("userOrOrganizationHandle" in parsed) {
        return `${parsed.userOrOrganizationHandle}/${slugWithShortId}`;
    }
    // Fallback for the Record<string, never> case (shouldn't happen in practice)
    return slugWithShortId;
}

// ----- Constituent functions of the above main entrypoint functions:

export function getDocumentUrlFromUid(documentUid: DocumentUid, options: GetDocumentUrlOptions = {}): string {
    // UID-based routes (/projects/:uid) don't support path-based editor sub-paths,
    // so strip editorSubPath to let getUrlParams fall back to ?editor= query param.
    const uidOptions: GetDocumentUrlOptions = options.urlParams?.editorSubPath
        ? {...options, urlParams: {...options.urlParams, editorSubPath: undefined}}
        : options;
    return `${PROJECT_BY_UID.replace(":documentUid", documentUid.toLowerCase())}${getUrlParams(uidOptions)}`;
}

export function getAbsoluteDocumentUrlFromUid(documentUid: DocumentUid, options: GetDocumentUrlOptions = {}): string {
    return toAbsoluteUrl(getDocumentUrlFromUid(documentUid, options));
}

function getDocumentUrlFromSlug(document: DocumentDataForUrlRouting, options: GetDocumentUrlOptions): string {
    const urlParts: string[] = ["/"];

    if (document.enterprise_owner_uid) {
        if (!document.enterprise_owner_handle) {
            // This won't usually happen, because callers should go through getDocumentUrl, which protects against this code path
            throw new ReverseUrlRoutingError(
                "Cannot route to an enterprise-owned project without an enterprise_owner_handle",
            );
        }

        urlParts.push("enterprise", document.enterprise_owner_handle);

        if (!document.organization_owner_is_enterprise_default) {
            urlParts.push("-", document.owner_handle);
        }
    } else {
        urlParts.push(document.owner_handle);
    }

    if (isLocalSlug(document.slug)) {
        if (!options.baseSlugForLocalComponent) {
            // This won't usually happen, because callers should go through getDocumentUrl, which protects against this code path
            throw new ReverseUrlRoutingError("Cannot create local document URL without a baseSlug");
        }

        urlParts.push(options.baseSlugForLocalComponent, document.slug.slice("local:".length));
    } else {
        const shortIdValue =
            typeof document.short_id === "string" ? document.short_id : document.short_id?.value;
        urlParts.push(
            options.enableShortIdUrls !== false
                ? formatSlugWithShortId(document.slug, shortIdValue)
                : document.slug,
        );
    }

    // Append editor sub-path if provided (new path-based routing).
    // Only for non-local-component URLs — local component URLs use a legacy format
    // where the component slug occupies the sub-path position, so appending an
    // editorSubPath would create an unparseable URL.
    if (options.urlParams?.editorSubPath && !isLocalSlug(document.slug)) {
        urlParts.push(options.urlParams.editorSubPath);
    }

    // For local component URLs, editorSubPath can't be in the path, so strip it
    // to let getUrlParams fall back to the ?editor= query param.
    const effectiveOptions: GetDocumentUrlOptions =
        options.urlParams?.editorSubPath && isLocalSlug(document.slug)
            ? {...options, urlParams: {...options.urlParams, editorSubPath: undefined}}
            : options;

    return `${joinPathParts(...urlParts)}${getUrlParams(effectiveOptions)}`;
}

function getUrlParams(options: GetDocumentUrlOptions): string {
    if (Object.entries(options?.urlParams ?? {}).length === 0) {
        return "";
    }

    const searchParams = new URLSearchParams();

    if (options?.urlParams?.embed) {
        searchParams.set("embed", "1");
    }

    // When editorSubPath is provided, the editor mode is encoded in the path — skip the query param.
    // Only fall back to ?editor= when no sub-path is set (legacy URL generation).
    if (options?.urlParams?.editorMode && !options?.urlParams?.editorSubPath) {
        searchParams.set("editor", queryParamValueForEditorState(options.urlParams ?? {}));
    }

    if (options?.urlParams?.fit) {
        searchParams.set("fit", options?.urlParams?.fit);
    }

    const paramString = searchParams.toString();
    return paramString ? `?${paramString}` : "";
}

export function queryParamValueForEditorState(params: DocumentUrlParams): string {
    if (params.editorMode === EditorModes.pcb) {
        if (params.cameraMode === CameraMode.two_d) {
            return "pcb_2d";
        } else {
            return "pcb_3d";
        }
    }

    if (params.editorMode === EditorModes.files) {
        return "files";
    }

    return params.editorMode ?? EditorModes.schematic;
}

/**
 * Returns the path-based editor sub-path for the given editor state.
 * This is the new path-based equivalent of queryParamValueForEditorState().
 *
 * Returns `undefined` when `editorMode` is `undefined` — callers should fall back to
 * query-param-based routing (or skip URL rewriting) in that case.
 *
 * The `docSlug` parameter is URL-encoded before being embedded in the sub-path.
 *
 * Examples:
 *   - EditorModes.schematic → "schematic"
 *   - EditorModes.pcb + CameraMode.two_d → "pcb/2d"
 *   - EditorModes.pcb + CameraMode.three_d → "pcb/3d"
 *   - EditorModes.pcb (no camera mode) → "pcb"
 *   - EditorModes.code → "code"
 *   - EditorModes.files → "files"
 *   - EditorModes.files + doc slug → "files/{docSlug}"
 *   - undefined editorMode → undefined
 */
export function editorSubPathForEditorState(params: {
    editorMode?: EditorModes;
    cameraMode?: CameraMode;
    docSlug?: string;
    /** Optional doc UID — when provided, appends a short UID suffix to prevent slug collisions */
    docUid?: string;
    /** Proper Feistel short ID. Takes precedence over docUid.slice(0, 8) when provided. */
    docShortId?: string;
}): string | undefined {
    if (params.editorMode === undefined) {
        return undefined;
    }

    if (params.editorMode === EditorModes.pcb) {
        if (params.cameraMode === CameraMode.two_d) {
            return "pcb/2d";
        }
        if (params.cameraMode === CameraMode.three_d) {
            return "pcb/3d";
        }
        return "pcb";
    }

    if (params.editorMode === EditorModes.files) {
        if (params.docSlug) {
            const encodedSlug = encodeURIComponent(params.docSlug);
            // Prefer the proper Feistel short ID; fall back to uid prefix for legacy docs
            const shortIdSuffix = params.docShortId ?? (params.docUid ? params.docUid.slice(0, DOC_UID_SHORT_LENGTH) : undefined);
            if (shortIdSuffix) {
                return `files/${encodedSlug}~${shortIdSuffix}`;
            }
            return `files/${encodedSlug}`;
        }
        return "files";
    }

    if (params.editorMode === EditorModes.code) {
        return "code";
    }

    if (params.editorMode === EditorModes.nets) {
        return "nets";
    }

    if (params.editorMode === EditorModes.components) {
        return "components";
    }

    if (params.editorMode === EditorModes.bom) {
        return "bom";
    }

    // EditorModes.schematic or any unrecognized value falls through to schematic.
    // This mirrors queryParamValueForEditorState().
    return "schematic";
}

/**
 * Parses a path-based editor sub-path back into editor state.
 * This is the approximate inverse of editorSubPathForEditorState().
 *
 * Note: The round-trip is asymmetric for "parts" sub-paths — `editorStateForEditorSubPath("parts")`
 * returns `{editorMode: pcb, cameraMode: three_d}`, but `editorSubPathForEditorState({editorMode: pcb,
 * cameraMode: three_d})` returns `"pcb/3d"`, not `"parts"`. Callers that need to preserve the original
 * "parts" URL should retain the raw sub-path string rather than re-deriving it from editor state.
 *
 * Returns undefined for unrecognized sub-paths (they may be legacy local component slugs).
 */
export function editorStateForEditorSubPath(subPath: string): {
    editorMode: EditorModes;
    cameraMode: CameraMode | undefined;
    docSlug: string | undefined;
} | undefined {
    if (subPath === "schematic") {
        return {editorMode: EditorModes.schematic, cameraMode: undefined, docSlug: undefined};
    }
    if (subPath === "code") {
        return {editorMode: EditorModes.code, cameraMode: undefined, docSlug: undefined};
    }
    if (subPath === "pcb") {
        return {editorMode: EditorModes.pcb, cameraMode: undefined, docSlug: undefined};
    }
    if (subPath === "pcb/2d") {
        return {editorMode: EditorModes.pcb, cameraMode: CameraMode.two_d, docSlug: undefined};
    }
    if (subPath === "pcb/3d") {
        return {editorMode: EditorModes.pcb, cameraMode: CameraMode.three_d, docSlug: undefined};
    }
    if (subPath === "files") {
        return {editorMode: EditorModes.files, cameraMode: undefined, docSlug: undefined};
    }
    if (subPath.startsWith("files/")) {
        // Strip the UID suffix ("~abc12345") from the slug for display/matching purposes
        const rawSlug = subPath.slice("files/".length);
        const tildeIndex = rawSlug.lastIndexOf("~");
        const slug = tildeIndex !== -1 ? rawSlug.slice(0, tildeIndex) : rawSlug;
        return {editorMode: EditorModes.files, cameraMode: undefined, docSlug: decodeURIComponent(slug)};
    }
    if (subPath === "nets") {
        return {editorMode: EditorModes.nets, cameraMode: undefined, docSlug: undefined};
    }
    if (subPath === "components") {
        return {editorMode: EditorModes.components, cameraMode: undefined, docSlug: undefined};
    }
    if (subPath === "bom") {
        return {editorMode: EditorModes.bom, cameraMode: undefined, docSlug: undefined};
    }
    if (subPath === "parts" || subPath.startsWith("parts/")) {
        // Parts view defaults to PCB 3D — the consumer will determine camera mode from document data
        return {editorMode: EditorModes.pcb, cameraMode: CameraMode.three_d, docSlug: undefined};
    }
    return undefined;
}

/**
 * Maps a legacy `?editor=` query param value to the equivalent path-based sub-path.
 * Used during backward-compat URL rewrites.
 *
 * Mapping:
 *   "schematic" → "schematic"
 *   "code"      → "code"
 *   "pcb_2d"    → "pcb/2d"
 *   "pcb"       → "pcb"
 *   "pcb_3d"    → "pcb/3d"
 */
function editorQueryParamToSubPath(queryParamValue: string): string | undefined {
    switch (queryParamValue) {
        case "schematic":
            return "schematic";
        case "code":
            return "code";
        case "pcb_2d":
            return "pcb/2d";
        case "pcb":
            return "pcb";
        case "pcb_3d":
            return "pcb/3d";
        case "nets":
            return "nets";
        case "components":
            return "components";
        case "bom":
            return "bom";
        default:
            return undefined;
    }
}

/**
 * Rewrites a legacy URL with `?editor=` query params to the equivalent
 * path-based URL. Returns the new URL string if a rewrite is needed, or undefined if
 * the URL is already in the new format or can't be rewritten.
 *
 * This is a pure function — it does NOT call replaceState. The caller is responsible
 * for performing the actual URL rewrite.
 *
 * Examples:
 *   "/handle/project?editor=pcb_2d"           → "/handle/project/pcb/2d"
 *   "/handle/project?editor=code&thread=abc"   → "/handle/project/code?thread=abc"
 *   "/handle/project/schematic"                → undefined (already path-based)
 *   "/projects/uid123?editor=pcb_2d"           → undefined (UID routes keep query params)
 */
export function rewriteLegacyEditorUrl(pathname: string, search: string): string | undefined {
    const params = new URLSearchParams(search);
    const editorParam = params.get("editor");
    const changeIdParam = params.get("changeId");

    // Nothing to rewrite if no legacy params present
    if (!editorParam && !changeIdParam) {
        return undefined;
    }

    // Parse the pathname to check if it's already path-based or if it's a UID route
    let parsed: ParsedDocumentPath;
    try {
        parsed = parseDocumentPathname(pathname);
    } catch {
        return undefined;
    }

    // UID-based routes don't support sub-paths — keep query params
    if ("uid" in parsed) {
        return undefined;
    }

    const basePath = "/" + getBasePathFromParsed(parsed);

    // Already has a path-based editor sub-path — don't double-rewrite the editor part
    if (parsed.editorSubPath) {
        // Strip redundant legacy params and rewrite ?changeId= into the path if needed
        const needsChangeIdRewrite = changeIdParam && parsed.historyId !== changeIdParam;
        if (editorParam || needsChangeIdRewrite) {
            params.delete("editor");
            if (needsChangeIdRewrite) {
                // Rewrite ?changeId= into the path: /history/{changeId}/{editorSubPath}
                params.delete("changeId");
                const remaining = params.toString();
                const rewrittenPath = `${basePath}/history/${changeIdParam}/${parsed.editorSubPath}`;
                return `${rewrittenPath}${remaining ? `?${remaining}` : ""}`;
            }
            const remaining = params.toString();
            return `${pathname}${remaining ? `?${remaining}` : ""}`;
        }
        return undefined;
    }

    // Build the editor sub-path from the query param
    let subPath: string | undefined;
    if (editorParam) {
        subPath = editorQueryParamToSubPath(editorParam);
    }

    // Handle standalone ?changeId= rewrite (no ?editor=, just moving changeId to path)
    if (!subPath && changeIdParam) {
        // Only rewrite if the changeId isn't already in the path
        if (parsed.historyId !== changeIdParam) {
            const existingEditorSubPath = parsed.editorSubPath ? `/${parsed.editorSubPath}` : "";
            const rewrittenPath = `${basePath}/history/${changeIdParam}${existingEditorSubPath}`;
            params.delete("changeId");
            const remaining = params.toString();
            return `${rewrittenPath}${remaining ? `?${remaining}` : ""}`;
        }
        return undefined;
    }

    if (!subPath) {
        return undefined;
    }

    // Append local component slug path if present (legacy component URL with editor param)
    let fullPath: string;
    if (parsed.localComponentSlug) {
        // Legacy: /:handle/:baseSlug/:componentSlug?editor=X
        // Rewrite to: /:handle/:baseSlug/parts/:componentSlug/X
        // But for now, just strip the editor param — the local component URL structure stays
        params.delete("editor");
        const remaining = params.toString();
        return `${pathname}${remaining ? `?${remaining}` : ""}`;
    }

    fullPath = `${basePath}/${subPath}`;

    // Preserve history segment if present, and incorporate ?changeId= into the path
    const effectiveHistoryId = changeIdParam ?? parsed.historyId;
    if (effectiveHistoryId) {
        fullPath = `${basePath}/history/${effectiveHistoryId}/${subPath}`;
    }

    // Remove legacy params, keep everything else
    params.delete("editor");
    params.delete("changeId");
    const remaining = params.toString();

    return `${fullPath}${remaining ? `?${remaining}` : ""}`;
}

/**
 * Extracts the base project path from a pathname, stripping any editor sub-paths,
 * history segments, and local component slugs.
 *
 * Examples:
 *   - "/handle/project" → "/handle/project"
 *   - "/handle/project/schematic" → "/handle/project"
 *   - "/handle/project/pcb/2d" → "/handle/project"
 *   - "/handle/project/history/abc/pcb" → "/handle/project"
 *   - "/enterprise/ent/project/code" → "/enterprise/ent/project"
 *   - "/enterprise/ent/-/org/project/files/doc" → "/enterprise/ent/-/org/project"
 */
export function getProjectBasePath(pathname: string): string {
    try {
        const parsed = parseDocumentPathname(pathname);
        if ("uid" in parsed) {
            return `/projects/${parsed.uid}`;
        }
        return "/" + getBasePathFromParsed(parsed);
    } catch {
        return pathname;
    }
}

/**
 * Builds a URL string for an editor mode change, with guards for UID-based routes
 * and local component URLs that don't support path-based editor sub-paths.
 *
 * On slug-based routes, returns a path-based URL like `/handle/project/schematic`.
 * On UID-based routes (`/projects/:uid`) or local component URLs
 * (`/handle/host/component`), falls back to `?editor=` query params to avoid
 * producing URLs that no route can serve.
 *
 * @param pathname - The current `window.location.pathname`
 * @param search - The current `window.location.search`
 * @param editorMode - The target editor mode
 * @param cameraMode - Optional camera mode (for PCB 2D/3D)
 * @param docSlug - Optional document slug (for files mode)
 * @returns The full URL string (path + search) to pass to `pushState` / `replaceState`
 */
export function buildEditorModeUrl(
    pathname: string,
    search: string,
    editorMode: EditorModes,
    cameraMode?: CameraMode,
    docSlug?: string,
    docUid?: string,
    docShortId?: string,
): string {
    const params = new URLSearchParams(search);
    let useQueryParamFallback = false;

    try {
        const parsed = parseDocumentPathname(pathname);
        // UID-based routes don't have sub-path variants in DocumentRoutes
        if ("uid" in parsed) {
            useQueryParamFallback = true;
        }
        // Local component URLs use the sub-path position for the component slug
        if ("slug" in parsed && parsed.localComponentSlug) {
            useQueryParamFallback = true;
        }
        // History mode: preserve /history/{id} and only change the editor sub-path
        if (!useQueryParamFallback && "slug" in parsed && parsed.historyId) {
            const basePath = getProjectBasePath(pathname);
            const subPath = editorSubPathForEditorState({editorMode, cameraMode, docSlug, docUid, docShortId});
            params.delete("editor");
            params.delete("docId");
            const paramString = params.toString();
            return `${basePath}/history/${parsed.historyId}/${subPath}${paramString ? `?${paramString}` : ""}`;
        }
    } catch {
        // Not a document route — fall back to query params to be safe
        useQueryParamFallback = true;
    }

    if (useQueryParamFallback) {
        params.set("editor", queryParamValueForEditorState({editorMode, cameraMode}));
        const paramString = params.toString();
        return `${pathname}${paramString ? `?${paramString}` : ""}`;
    }

    // Normal slug-based route: use path-based editor sub-paths
    const basePath = getProjectBasePath(pathname);
    const subPath = editorSubPathForEditorState({editorMode, cameraMode, docSlug, docUid, docShortId});
    params.delete("editor");
    params.delete("docId");
    const paramString = params.toString();
    return `${basePath}/${subPath}${paramString ? `?${paramString}` : ""}`;
}

// ----- Deprecated functions that should be cleaned up

export function buildDocumentPathFromSlugs(baseSlug: string, componentSlug?: string): string {
    return joinPathParts(baseSlug, componentSlug).toLowerCase();
}

/**
 * @deprecated Does not support enterprises
 */
export function getRelativeUrl(
    documentUid: string,
    documentSlug: string,
    documentOwnerHandle: string,
    baseSlug?: string,
    params: DocumentUrlParams = {},
) {
    if (documentOwnerHandle && (!isLocalSlug(documentSlug) || baseSlug)) {
        return getRelativeUrlFromSlug(documentOwnerHandle, documentSlug, baseSlug, params);
    } else if (documentUid) {
        return getDocumentUrlFromUid(documentUid);
    } else {
        return "/"; // QUESTION: what is this for?
    }
}

/**
 * NOTE: The slugify feature was changed in #3260 in Nov 2022 without migrating
 * the stored data, so some links generated by
 * getRelativeUrlFromDocumentName will be invalid and return 404.
 *
 * @see getDocumentUrlFromUid
 * @deprecated The slug is stored source-of-truth information on the document and cannot be calculated from the name
 * @deprecated Does not support local components
 * @deprecated Does not support enterprises
 */
export function getRelativeUrlFromDocumentName(
    userHandle: string,
    documentName: string,
    params: DocumentUrlParams = {},
) {
    return getRelativeUrlFromSlug(userHandle, slugifyDocumentName(documentName), undefined, params);
}

/**
 * @deprecated Does not support enterprises
 */
function getRelativeUrlFromSlug(ownerHandle: string, slug: string, baseSlug?: string, params: DocumentUrlParams = {}) {
    let path;
    if (isLocalSlug(slug)) {
        if (!baseSlug) {
            // Catch any callers that call this improperly on a local document
            throw new Error(
                "Cannot create local document URL without baseSlug. Provide baseSlug or use uid-based URL instead",
            );
        }
        path = joinPathParts("/", ownerHandle, baseSlug, slug.slice("local:".length));
    } else {
        path = joinPathParts("/", ownerHandle, slug);
    }

    if (params.editorMode || params.embed) {
        const searchParams = new URLSearchParams();

        if (params.editorMode) {
            searchParams.set("editor", queryParamValueForEditorState(params));
        }

        if (params.embed) {
            searchParams.set("embed", "1");
        }

        if (params.fit) {
            searchParams.set("fit", params.fit);
        }

        return path + "?" + searchParams.toString();
    }

    return path;
}
